import { Fragment, h } from "preact";
import { useContext, useEffect, useLayoutEffect } from "preact/hooks";
import { useDispatch, useSelector } from "react-redux";

import Alert from "./Alert.jsx";
import Button from "./Button.jsx";
import AppSidebar from "./AppSidebar.jsx";
import LinearLoadingIndicator from "./LinearLoadingIndicator.jsx";
import NaviDrawer from "./NaviDrawer.jsx";
import i18n from "../i18n.js";
import { classNames } from "../helpers.js";
import { setIsMobileView } from "../store/index";
import AppHeader from "./AppHeader.jsx";
import AppFooter from "./AppFooter.jsx";
import Toasts from "./Toasts.jsx";
import { PreviewModeContext } from "../contexts.js";

const MOBILE_BREAKPOINT = 991;

export default function AppLayout({
  children,
  ornamented,
  unpadded,
  ...props
}) {
  const previewMode = useContext(PreviewModeContext);
  const showSessionTooBig = useSelector(
    (state) => state.ui.showSessionTooBigModal
  );
  const isMobileView = useSelector((state) => state.ui.isMobileView);
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.ui.loading);
  let resizeTimeout = null;

  useLayoutEffect(() => {
    dispatch(setIsMobileView(window.innerWidth < MOBILE_BREAKPOINT));
  }, []);

  function onResize() {
    if (resizeTimeout) window.clearTimeout(resizeTimeout);
    resizeTimeout = window.setTimeout(() => {
      dispatch(setIsMobileView(window.innerWidth < MOBILE_BREAKPOINT));
    }, 150);
  }

  useEffect(() => {
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  });

  let header = null;
  if (!props.noHeader) {
    header = props.header ? (
      props.header
    ) : (
      <AppHeader title={props.title} loading={loading} />
    );
  }
  let footer = null;
  if (!props.noFooter) {
    footer = props.footer ? props.footer : <AppFooter />;
  }

  return (
    <Fragment>
      <div
        class={classNames(
          "app-layout",
          isMobileView && "app-layout--mobile-view",
          ornamented && "app-layout--ornamented",
          unpadded && "app-layout--unpadded",
          props.class
        )}
      >
        <AppSidebar class="app-layout__sidebar" previewMode={previewMode} />
        <div
          class={classNames(
            "app-layout__contentPreWrap",
            header && "app-layout__contentPreWrap--withHeader",
            props.headerWithFilter &&
              "app-layout__contentPreWrap--withHeaderWithFilter"
          )}
        >
          <Toasts />
          {header && <div class="app-layout__content-header">{header}</div>}
          <div
            class={classNames(
              "app-layout__contentWrap",
              props.forProfile && "app-layout__contentWrap--forProfile"
            )}
          >
            {(props.loading || loading) && (
              <LinearLoadingIndicator class="app-layout__loading" />
            )}
            <main class="app-layout__content">
              {showSessionTooBig && (
                <Alert error>
                  <div>
                    <p>{i18n.t("errors.session_too_big")}</p>
                    <Button secondary href="/register">
                      {i18n.t("actions.signup")}
                    </Button>
                  </div>
                </Alert>
              )}
              {children}
            </main>
            {footer && <footer class="app-layout__footer">{footer}</footer>}
          </div>
        </div>
        {isMobileView && <NaviDrawer previewMode={previewMode} />}
      </div>
    </Fragment>
  );
}
